import React, { useState, useEffect, useCallback } from 'react';
import './UserTokenUsageHistory.css';

/**
 * User Token Usage History Component
 * Displays detailed chat history with token consumption
 */
export const UserTokenUsageHistory = ({ userId, maxDays = 30 }) => {
  const [history, setHistory] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);

  const fetchUsageHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/sro/usage-history?days=${maxDays}&limit=${limit}&offset=${offset}`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch usage history');

      const data = await response.json();
      let filteredHistory = data.history || [];

      if (filterProvider !== 'all') {
        filteredHistory = filteredHistory.filter(h => h.provider === filterProvider);
      }
      if (filterType !== 'all') {
        filteredHistory = filteredHistory.filter(h => h.message_type === filterType);
      }

      setHistory(filteredHistory);
      setStats(data.stats);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching usage history:', err);
    } finally {
      setLoading(false);
    }
  }, [filterProvider, filterType, limit, maxDays, offset]);

  useEffect(() => {
    fetchUsageHistory();
  }, [fetchUsageHistory, userId]);

  const handleExport = async () => {
    try {
      const response = await fetch(
        `/api/admin/usage-report?userId=${userId}&startDate=${new Date(Date.now() - maxDays * 24 * 60 * 60 * 1000).toISOString()}&endDate=${new Date().toISOString()}&exportCsv=true`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usage-history-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to export: ' + err.message);
    }
  };

  if (loading && !history) {
    return <div className="usage-history loading">Loading usage history...</div>;
  }

  return (
    <div className="usage-history">
      <div className="history-header">
        <h3>Token Usage History</h3>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={handleExport}>
            📥 Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          Error: {error}
        </div>
      )}

      {stats && (
        <div className="usage-summary">
          <div className="summary-card">
            <div className="summary-label">Total Chats</div>
            <div className="summary-value">{stats.summary?.total_calls || 0}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Tokens</div>
            <div className="summary-value">{(stats.summary?.total_tokens || 0).toLocaleString()}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Average/Chat</div>
            <div className="summary-value">{Math.round(stats.summary?.total_tokens / (stats.summary?.total_calls || 1) || 0)}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Cost</div>
            <div className="summary-value">${stats.summary?.total_cost ? parseFloat(stats.summary.total_cost).toFixed(2) : '0.00'}</div>
          </div>
        </div>
      )}

      <div className="history-filters">
        <select value={filterProvider} onChange={(e) => setFilterProvider(e.target.value)}>
          <option value="all">All Providers</option>
          <option value="gemini">Gemini</option>
          <option value="openai">OpenAI</option>
          <option value="deepseek">DeepSeek</option>
          <option value="openrouter">OpenRouter</option>
        </select>

        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="all">All Types</option>
          <option value="chat">Chat</option>
          <option value="validation">Validation</option>
          <option value="extraction">Extraction</option>
        </select>
      </div>

      {history && history.length > 0 ? (
        <div className="history-table">
          <table>
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>Type</th>
                <th>Provider</th>
                <th>Prompt Tokens</th>
                <th>Output Tokens</th>
                <th>Total Tokens</th>
                <th>Cost (USD)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item, idx) => (
                <tr key={idx}>
                  <td>{new Date(item.created_at).toLocaleString()}</td>
                  <td><span className="badge badge-info">{item.message_type}</span></td>
                  <td><span className="badge badge-success">{item.provider}</span></td>
                  <td className="text-right">{(item.prompt_tokens || 0).toLocaleString()}</td>
                  <td className="text-right">{(item.completion_tokens || 0).toLocaleString()}</td>
                  <td className="text-right"><strong>{(item.total_tokens || 0).toLocaleString()}</strong></td>
                  <td className="text-right">${item.cost_usd ? parseFloat(item.cost_usd).toFixed(6) : '0.000000'}</td>
                  <td>
                    <span className={`badge badge-${item.status === 'success' ? 'success' : 'error'}`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          No usage history found for the selected filters.
        </div>
      )}

      {history && (
        <div className="pagination">
          <button
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            className="btn btn-small"
          >
            ← Previous
          </button>
          <span className="pagination-info">
            Showing {offset + 1} - {Math.min(offset + limit, (offset + limit) + (history.length % limit))}
          </span>
          <button
            onClick={() => setOffset(offset + limit)}
            disabled={history.length < limit}
            className="btn btn-small"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default UserTokenUsageHistory;
